import type { ClientBuilderPoliciesAndRoles } from "api-types/builders/buildersResponses";
import type { BuilderProducts } from "api-types/builders/builderTypes";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type {
  ClientBuilderUserHydratedWithRoles,
  ClientBuilderUserPermission,
  ClientBuilderUserWithRoles,
} from "api-types/builderUsers/builderUsersResponses";
import type { ClientFeatureFlagPublicData } from "api-types/featureFlags/featureFlagsResponses";
import type { StudioPrefs } from "api-types/users/usersRequests";
import type { ClientUser, ClientUserHydratedWithRoles } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import type { FeatureSet } from "common/constants/features";
import Flags from "common/constants/flags";
import { INTERCOM_APP_ID } from "common/constants/marketing";
import { posthog } from "lib/analytics";
import { clearRumUser, setRumUser } from "lib/datadogRum";
import { getFeatureFlagPublicDataURL } from "lib/endpointHelpers";
import { useFetchNoAuth, useFetchNoHandle, useUser } from "lib/fetcher";
import { useAppRouterParam } from "lib/routeHelpers";
import {
  getCurrentBuilderUser,
  getBuilderUserRoleName,
  getCurrentUserEnabledProducts,
  getUserEnabledFeatures,
  userIsSuperAdmin,
  userHasPermission,
} from "lib/security/shared";
import getUserFullName from "lib/utils/userName";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";

export interface UserContextState {
  readonly user: ClientUserHydratedWithRoles | null;
  readonly builderUser?: ClientBuilderUserHydratedWithRoles;
  readonly builderUserRoleName?: string;
  readonly builder?: ClientBuilder;
  readonly builderPoliciesAndRoles?: ClientBuilderPoliciesAndRoles;
  readonly enabledFeatures: FeatureSet;
  readonly featuresLoaded: boolean;
  readonly studioPrefs: StudioPrefs;
  readonly enabledProducts: BuilderProducts;
  readonly isSuperAdmin: boolean;
  readonly hasPermission: (
    permission: ClientBuilderUserPermission,
    context?: {
      userOverride?: Pick<ClientUser, "adminRole" | "tempUserRole">;
      builderUserOverride?: Pick<ClientBuilderUserWithRoles, "id" | "userId" | "roles" | "builderId">;
      resourceId?: string;
    },
  ) => boolean;

  readonly userFetchError?: unknown;
  readonly userLoading?: boolean;

  readonly builderFetchError?: unknown;
  readonly builderLoading?: boolean;
}

const initialUserContextState: UserContextState = {
  user: null,
  isSuperAdmin: false,
  enabledFeatures: {} as FeatureSet,
  featuresLoaded: false,
  enabledProducts: {} as BuilderProducts,
  studioPrefs: { keyboardShortcuts: [] },
  hasPermission: () => false,
};

export const UserContext = createContext<UserContextState>(initialUserContextState);

interface UserContextProviderProps {
  children: ReactNode;
}

export default function UserContextProvider({ children }: UserContextProviderProps) {
  const builderId = useAppRouterParam("builderId");
  const { user, loading: userLoading, error: userFetchError } = useUser();
  const builderUser = useMemo(() => getCurrentBuilderUser(user, builderId), [user, builderId]);
  const builderUserRoleName = getBuilderUserRoleName(user, builderUser ?? undefined);

  const {
    data: builder,
    loading: builderLoading,
    error: builderFetchError,
  } = useFetchNoHandle<ClientBuilder>(`${WEB_API_BASE_PATH}/builders/${builderId}`, [builderId, user]);

  const {
    data: builderPoliciesAndRoles,
    loading: builderPoliciesLoading,
    error: builderPoliciesFetchError,
  } = useFetchNoHandle<ClientBuilderPoliciesAndRoles>(`${WEB_API_BASE_PATH}/builders/${builderId}/policies-roles`, [
    builderId,
    user,
  ]);

  // Fetch regardless of builderId — env overrides and DB default values are builder-independent, so
  // gating on a falsy builderId would skip it and leave env-enabled flags unresolved on builder-less
  // routes. Builder overrides still layer in via the builderId URL param when present.
  const { data: featureFlagPublicData, loading: featureFlagPublicDataLoading } =
    useFetchNoAuth<ClientFeatureFlagPublicData>(getFeatureFlagPublicDataURL({ builderId }), []);

  const enabledFeatures = useMemo(
    () =>
      getUserEnabledFeatures(
        user,
        builder,
        featureFlagPublicData?.environmentSettingOverrides,
        featureFlagPublicData?.defaultValues,
      ),
    [user, builder, featureFlagPublicData?.environmentSettingOverrides, featureFlagPublicData?.defaultValues],
  );
  const enabledProducts = useMemo(() => getCurrentUserEnabledProducts(builder), [builder]);

  useEffect(() => {
    if (user) {
      if (!posthog._isIdentified()) {
        posthog.identify(user.id, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: builderUserRoleName,
          environment: Flags.DB_ENV,
        });
      }

      if (user?.id) {
        setRumUser(user.id, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: builderUserRoleName,
          environment: Flags.DB_ENV,
        });
      }
    } else {
      // logout
      posthog.reset();
      clearRumUser();
    }
  }, [user]);

  // This could be replaced by groups, which is a paid feature.
  useEffect(() => {
    if (posthog._isIdentified()) {
      posthog.setPersonProperties({ builder: builder?.companyName });
    }
  }, [builder]);

  useEffect(() => {
    const loadIntercom = async () => {
      // show release notes, link user/builder records in intercom
      // see docs: https://developers.intercom.com/installing-intercom/web/attributes-objects
      if (user) {
        const { default: Intercom } = await import("@intercom/messenger-js-sdk");

        Intercom({
          alignment: "right",
          horizontal_padding: 20,
          vertical_padding: 20,
          app_id: INTERCOM_APP_ID,
          user_id: user.id ?? undefined,
          companies: builder?.id
            ? [
                {
                  company_id: builder.id,
                  company_name: builder.companyName,
                  website: builder.website,
                },
              ]
            : undefined,
          name: getUserFullName(user),
          email: user.email ?? undefined,
          created_at: new Date(user.createdAt).getTime(),
        });
      }
    };

    if (!Flags.IS_DEV_MODE) {
      void loadIntercom();
    }
  }, [user, builder]);

  const userContext: UserContextState = {
    isSuperAdmin: userIsSuperAdmin(user),
    hasPermission: useCallback(
      (permission, context) =>
        userHasPermission(permission, {
          user: context?.userOverride ?? user,
          builderUser: context?.builderUserOverride ?? builderUser ?? undefined,
          builderPoliciesAndRoles,
          resourceId: context?.resourceId,
        }),
      [user, builderUser, builderPoliciesAndRoles],
    ),
    enabledFeatures,
    featuresLoaded: !userLoading && !builderLoading && !featureFlagPublicDataLoading,
    enabledProducts,
    studioPrefs: (user?.studioPrefs as StudioPrefs) || { keyboardShortcuts: [] },
    user,
    builderUser: builderUser ?? undefined,
    builderUserRoleName: builderUserRoleName ?? undefined,
    builder,
    builderPoliciesAndRoles,
    userFetchError,
    userLoading,
    builderFetchError: builderFetchError ?? builderPoliciesFetchError,
    builderLoading: builderLoading || builderPoliciesLoading || featureFlagPublicDataLoading,
  };

  return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>;
}

export function useUserContext() {
  const context = useContext(UserContext);
  const builderId = useAppRouterParam("builderId");

  const noUser = !context.user && !context.userLoading;
  // On public pages (no user), fetch builder
  const { data: builder, loading: publicBuilderLoading } = useFetchNoAuth<ClientBuilder>(
    `${WEB_API_BASE_PATH}/builders/${builderId}`,
    [builderId, noUser],
  );

  // Only consumed when there's no user; gate on noUser, not builderId (see above).
  const { data: featureFlagPublicData, loading: featureFlagPublicDataLoading } =
    useFetchNoAuth<ClientFeatureFlagPublicData>(getFeatureFlagPublicDataURL({ builderId }), [noUser]);

  const publicEnabledFeatures = useMemo(
    () =>
      featureFlagPublicData?.enabledFeatures ??
      getUserEnabledFeatures(
        null,
        builder,
        featureFlagPublicData?.environmentSettingOverrides,
        featureFlagPublicData?.defaultValues,
      ),
    [builder, featureFlagPublicData],
  );
  const publicEnabledProducts = useMemo(() => getCurrentUserEnabledProducts(builder), [builder]);

  // On public pages, when UserContext provider is skipped, still return enabledFeatures and builder.
  // Surface the public-fetch loading state as builderLoading so feature/product guards (`useFeatures`,
  // `useProducts`) wait for hydration before rejecting — without this, an empty enabledFeatures map
  // is briefly observable as "flag off" and triggers a 403 redirect on every public page mount.
  if (noUser) {
    return {
      ...context,
      user: null,
      builder,
      enabledFeatures: publicEnabledFeatures,
      enabledProducts: publicEnabledProducts,
      featuresLoaded: featureFlagPublicData !== undefined || (!featureFlagPublicDataLoading && builder !== undefined),
      builderLoading: publicBuilderLoading || featureFlagPublicDataLoading,
    };
  }
  return context;
}
